/**
 * Pane 2 — the Net Wire Calculator: the funding-to-title tie-out.
 *
 * The `NetWireCalculator` model (`funding_statements.py:251`) is explicitly two
 * blocks, and this pane renders them as two, in that order:
 *
 *   1. WIRE BUILD-DOWN (lender side, worksheet-canonical)
 *        initialAdvance = totalLoanAmount − totalHoldback
 *        netWireToTitle = initialAdvance + Σ credits − Σ fees − Σ escrows
 *                         − financedInterestReserve
 *      Rendered as a running-balance waterfall: every line shows its effect AND
 *      the balance after it, so a wrong number is traceable to the line that
 *      caused it instead of only to the total.
 *
 *   2. HUD BALANCE (title side)
 *        hudSources vs hudUses, variance = sourcesTotal − usesTotal, balanced
 *        exactly zero — no tolerance. Sources and uses sit side by side because that is the
 *        comparison — reading them stacked is what made this hard before.
 *
 * `hudCompareSource` says whether the title side came from a real HUD extract or
 * was PREPARED from loan fields, which is how much the variance is worth
 * trusting — so it is stated on the block, not buried in a corner chip.
 *
 * Every figure here is the backend's; the FE computes nothing but the running
 * balance, which is just a re-presentation of the same arithmetic.
 */
import { useMemo, useState } from "react";
import PropTypes from "prop-types";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import ReportProblemOutlined from "@mui/icons-material/ReportProblemOutlined";
import RemoveCircleOutlineRounded from "@mui/icons-material/RemoveCircleOutlineRounded";
import SwapHorizRounded from "@mui/icons-material/SwapHorizRounded";

import Flx from "../../../../../components/layout/Flx";
import Txt from "../../../../../components/typography/Txt";
import { useFundingStatementsHook } from "../../../../../hooks/useFundingStatementsHook";
import { AdaptiveRows, Disclosure, leftoverKeys } from "../adaptiveDetails";
import {
  Callout,
  EmptyLine,
  LineRow,
  Pill,
  SectionHead,
  StatCell,
  StatStrip,
  T,
  labelSx,
  money,
  num,
  signedMoney,
} from "../wireFundingShared";
import {
  lenderChargesMeaning,
  lenderChargesTieOut,
} from "../WireReconciliationBar";
import HudSourceChip from "./HudSourceChip";
import HudSourcePicker from "./HudSourcePicker";
import NewerHudVersionsBanner from "./NewerHudVersionsBanner";
import { categoryBadge } from "./StatementPane";
import {
  PaneEmpty,
  PaneHeader,
  PaneLoading,
  PaneShell,
  PaneStrip,
  StripCell,
} from "./PaneShell";

/** One waterfall step: label, effect on the wire, and the balance after it. */
const WaterfallRow = ({ label, badge, badgeTone, note, effect, balance, strong }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) 120px 140px",
      gap: 2,
      alignItems: "center",
      py: 0.9,
      borderBottom: `1px ${strong ? "solid" : "dashed"} ${strong ? T.line : T.line2}`,
    }}
  >
    <Flx column gap={0.2} sx={{ minWidth: 0 }}>
      <Flx ac gap={0.75} sx={{ minWidth: 0 }}>
        <Txt
          sx={{
            fontSize: 12.5,
            fontWeight: strong ? 700 : 400,
            color: strong ? T.ink : T.ink2,
          }}
        >
          {label}
        </Txt>
        {badge ? <Pill label={badge} tone={badgeTone} /> : null}
      </Flx>
      {note ? <Txt sx={{ fontSize: 11, color: T.faint }}>{note}</Txt> : null}
    </Flx>
    <Txt
      sx={{
        textAlign: "right",
        fontFamily: T.mono,
        fontSize: 12.5,
        color: effect == null ? T.faint : effect < 0 ? T.ink2 : T.green,
      }}
    >
      {effect == null ? "" : signedMoney(effect)}
    </Txt>
    <Txt
      sx={{
        textAlign: "right",
        fontFamily: T.mono,
        fontSize: strong ? 14 : 12.5,
        fontWeight: strong ? 700 : 600,
        color: T.ink,
        letterSpacing: "-0.01em",
      }}
    >
      {money(balance)}
    </Txt>
  </Box>
);

/** hudSources / hudUses block. */
const HudColumn = ({ label, lines, total, emptyText }) => (
  <Box sx={{ minWidth: 0 }}>
    <SectionHead
      label={label}
      end={`${lines.length} ${lines.length === 1 ? "line" : "lines"}`}
    />
    {lines.length ? (
      lines.map((line, i) => (
        <LineRow
          key={line?.oid || `${line?.label}-${i}`}
          label={line?.label || "Line item"}
          {...categoryBadge(line?.category)}
          amount={money(line?.amount)}
          note={line?.calcNote}
        />
      ))
    ) : (
      <EmptyLine>{emptyText}</EmptyLine>
    )}
    <LineRow strong label={`${label} total`} amount={money(total)} />
  </Box>
);

const RENDERED_KEYS = new Set([
  "hudBalanceVariance",
  "hudCompareSource",
  "hudSource",
  "hudSourceWarnings",
  "hudNewerVersions",
  "hudLenderCharges",
  "lenderWithheldTotal",
  "lenderChargesVariance",
  "withheldLines",
  "hudSources",
  "hudUses",
  "hudSourcesTotal",
  "hudUsesTotal",
  "feeLines",
  "creditLines",
  "escrowLines",
  "initialAdvance",
  "netWireToTitle",
  "totalLoanAmount",
  "totalHoldback",
  "financedInterestReserve",
  "baseLoanAmount",
  "dutchInterest",
  "loanPurpose",
  "loanProductType",
  "fundingType",
  "fundingEntityName",
  "propertyAddresses",
  "syncLoanId",
  "generatedDate",
  "statementStatus",
  "statementType",
  "oid",
  "_id",
  "dealId",
  "documentOid",
  "lockedBy",
  "lockedAt",
  "acknowledgedBy",
  "acknowledgedAt",
  "acknowledgmentMethod",
]);

const TieOutPane = ({
  netWire,
  loading,
  failed,
  onViewPdf,
  onDownloadWorksheet,
  downloadingWorksheet,
  dealId,
  statementLocked,
  onHudSourceChanged,
  readingStatement,
}) => {
  const { setHudSource } = useFundingStatementsHook();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [hudBusy, setHudBusy] = useState(false);

  // The "newer version" banner's two actions (funding-statements.md
  // 2026-10-05): use the newer document, or keep the current one (the only
  // one allowed on a locked statement). Both return the rebuilt calculator.
  const postHudSource = (payload, onSuccessMsg) => {
    setHudBusy(true);
    setHudSource({
      dealId,
      payload,
      onSuccessMsg,
      onSuccessFn: (res) => onHudSourceChanged?.(res),
      onCompleteFn: () => setHudBusy(false),
    });
  };
  const variance = netWire?.hudBalanceVariance;
  const hasVariance = variance !== undefined && variance !== null;
  const varianceNum = Number(variance);
  // Zero tolerance — the two sides tie out exactly or they do not balance.
  const isBalanced =
    hasVariance && Number.isFinite(varianceNum) && varianceNum === 0;
  const fromExtract =
    netWire?.hudCompareSource && netWire.hudCompareSource !== "prepared";
  // Prepared title side = no HUD on file: the backend sends no variance and the
  // lines below are context only (funding-statements.md, closing assessment 5).
  const noHud = !hasVariance && netWire?.hudCompareSource === "prepared";

  const waterfall = useMemo(() => {
    if (!netWire) return [];
    const rows = [];
    let balance = num(netWire.initialAdvance);

    rows.push({
      label: "Initial advance",
      note:
        netWire.totalHoldback != null
          ? `Total loan ${money(netWire.totalLoanAmount)} − holdback ${money(netWire.totalHoldback)} (holdback is draw-funded, not wired)`
          : `Total loan ${money(netWire.totalLoanAmount)}`,
      effect: null,
      balance,
    });

    const push = (list, sign, fallbackLabel) =>
      (list || []).forEach((l, i) => {
        const effect = sign * num(l?.amount);
        balance += effect;
        rows.push({
          key: `${fallbackLabel}-${i}`,
          label: l?.label || fallbackLabel,
          note: l?.calcNote,
          ...categoryBadge(l?.category),
          effect,
          balance,
        });
      });

    push(netWire.creditLines, 1, "Credit");
    push(netWire.feeLines, -1, "Fee");
    push(netWire.escrowLines, -1, "Escrow");

    if (netWire.financedInterestReserve) {
      const effect = -num(netWire.financedInterestReserve);
      balance += effect;
      rows.push({
        key: "reserve",
        label: "Financed interest reserve",
        badge: "RESERVE",
        badgeTone: "info",
        note: "withheld from proceeds at close",
        effect,
        balance,
      });
    }

    rows.push({
      key: "net",
      label: "Net wire to title",
      badge: "WIRE",
      badgeTone: "accent",
      // Below zero = what we withheld exceeds the advance, so title wires the
      // difference to us (funding-statements.md, 2026-10-05).
      note:
        Number(netWire.netWireToTitle) < 0
          ? "Incoming from title — what we withheld exceeds the loan advance"
          : undefined,
      effect: null,
      // The backend's own figure, not the accumulator — if they ever differ,
      // showing the authoritative one is correct and the drift is visible.
      balance: netWire.netWireToTitle,
      strong: true,
    });

    return rows;
  }, [netWire]);

  const hudSources = Array.isArray(netWire?.hudSources) ? netWire.hudSources : [];
  const lender = lenderChargesTieOut(netWire);
  const lenderCharges = Array.isArray(netWire?.hudLenderCharges)
    ? netWire.hudLenderCharges
    : [];
  // The withheld side is the sources lines the backend sums into
  // lenderWithheldTotal: fees netted from the wire, holdback, financed reserve.
  const withheldSources = hudSources.filter((l) =>
    ["netted", "holdback", "reserve"].includes(l?.category),
  );
  const hudUses = Array.isArray(netWire?.hudUses) ? netWire.hudUses : [];
  const systemDetails = leftoverKeys(netWire, RENDERED_KEYS);

  const header = (
    <PaneHeader
      title="Net Wire Tie-out"
      badges={
        netWire?.hudCompareSource ? (
          <Pill
            label={fromExtract ? "HUD extract" : "Prepared"}
            tone={fromExtract ? "info" : "neutral"}
          />
        ) : null
      }
      subtitle="The lender-side build-down that produces the wire, and the title-side HUD balance it has to tie out against."
      actions={
        <>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FileDownloadOutlinedIcon />}
            onClick={onDownloadWorksheet}
            disabled={downloadingWorksheet}
          >
            {downloadingWorksheet ? "Preparing…" : "Worksheet .xlsx"}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<PictureAsPdfOutlinedIcon />}
            onClick={onViewPdf}
            disabled={!netWire}
          >
            View PDF
          </Button>
        </>
      }
    />
  );

  if (loading) return <PaneShell header={header}><PaneLoading /></PaneShell>;
  if (failed || !netWire) {
    return (
      <PaneShell header={header}>
        <PaneEmpty>
          The net wire calculation is not available for this loan yet. It is
          built for loans funded by our entity (direct origination and table
          funding) — loan purchases settle through the LPS on the acquisitions
          wire queue instead.
        </PaneEmpty>
      </PaneShell>
    );
  }

  return (
    <PaneShell
      header={header}
      strip={
        <PaneStrip
          meta={
            fromExtract
              ? "Title side read from an extracted HUD"
              : "Title side prepared from loan fields, not an extracted HUD"
          }
        >
          <StripCell
            label="Check"
            value={
              !hasVariance
                ? noHud
                  ? "No HUD to compare"
                  : "Nothing to tie out"
                : isBalanced
                  ? "Balanced"
                  : "Out of balance"
            }
            tone={!hasVariance ? T.faint : isBalanced ? T.green : T.red}
          />
          <StripCell
            label="Variance"
            value={hasVariance ? money(varianceNum) : "—"}
            tone={!hasVariance ? T.faint : isBalanced ? T.green : T.red}
          />
          <StripCell
            label="Lender charges"
            value={
              lender.state === "off"
                ? signedMoney(lender.variance)
                : lender.state === "tied"
                  ? "Tied out"
                  : "—"
            }
            tone={
              lender.state === "off"
                ? T.red
                : lender.state === "tied"
                  ? T.green
                  : T.faint
            }
          />
        </PaneStrip>
      }
    >
      <Flx column gap={3} fw>
        {/* The gate. */}
        <Callout
          tone={!hasVariance ? "neutral" : isBalanced ? "success" : "error"}
          icon={
            !hasVariance ? (
              <RemoveCircleOutlineRounded sx={{ fontSize: 20 }} />
            ) : isBalanced ? (
              <CheckCircleRounded sx={{ fontSize: 20 }} />
            ) : (
              <ReportProblemOutlined sx={{ fontSize: 20 }} />
            )
          }
          title={
            !hasVariance
              ? noHud
                ? "No HUD to compare"
                : "Nothing to tie out yet"
              : isBalanced
                ? "HUD balances"
                : "HUD is out of balance"
          }
          body={
            !hasVariance
              ? noHud
                ? "There is no extracted HUD on this loan, so there is nothing to balance. The title-side lines below are prepared from loan fields for context only — upload or extract the settlement statement to get a variance."
                : "No HUD sources or uses on file — the settlement statement has not been entered or extracted."
              : isBalanced
                ? "Sources and uses tie out exactly, against an extracted HUD."
                : "Sources and uses do not tie out. Resolve before the wire goes out."
          }
          figure={hasVariance ? money(varianceNum) : null}
        />

        <StatStrip>
          <StatCell label="Base loan" value={money(netWire.baseLoanAmount)} mono />
          <StatCell label="Total loan" value={money(netWire.totalLoanAmount)} mono />
          <StatCell
            label="Holdback"
            value={money(netWire.totalHoldback)}
            mono
            sub="not wired at close"
          />
          <StatCell
            label="Interest reserve"
            value={money(netWire.financedInterestReserve)}
            mono
            sub="financed, withheld"
          />
          <StatCell
            label="Interest accrual"
            value={
              netWire.dutchInterest == null
                ? "—"
                : netWire.dutchInterest
                  ? "Dutch"
                  : "Non-Dutch"
            }
            sub="drives per-diem"
          />
        </StatStrip>

        {/* Block 1 — the lender-side build-down. */}
        <Box>
          <SectionHead label="Wire build-down (lender side)" end="running balance" />
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr) 120px 140px",
              gap: 2,
              pt: 1,
              pb: 0.5,
            }}
          >
            <Txt sx={labelSx}>Line</Txt>
            <Txt sx={{ ...labelSx, textAlign: "right" }}>Effect</Txt>
            <Txt sx={{ ...labelSx, textAlign: "right" }}>Balance</Txt>
          </Box>
          {waterfall.map((row, i) => (
            <WaterfallRow key={row.key || `row-${i}`} {...row} />
          ))}
        </Box>

        {/* Block 2 — the title-side HUD balance. */}
        <Box>
          <SectionHead
            label="HUD balance (title side)"
            end={fromExtract ? "from HUD extract" : "prepared from loan fields"}
          />
          <HudSourceChip
            hudSource={netWire.hudSource}
            warnings={netWire.hudSourceWarnings}
            actions={
              dealId ? (
                <Button
                  size="small"
                  variant="text"
                  startIcon={<SwapHorizRounded />}
                  onClick={() => setPickerOpen(true)}
                >
                  Change
                </Button>
              ) : null
            }
          >
            {readingStatement ? (
              <Callout
                tone="info"
                title="Reading the statement…"
                body="The chosen statement had not been read yet and has been sent for extraction. Until it lands the balance uses lines prepared from loan fields and has no variance; this refreshes in about a minute."
              />
            ) : null}
            <NewerHudVersionsBanner
              versions={netWire.hudNewerVersions}
              locked={statementLocked}
              busy={hudBusy}
              onUse={(documentId) =>
                postHudSource({ documentId }, "Balancing against the newer statement")
              }
              onKeep={() =>
                postHudSource(
                  { acknowledgeNewerVersions: true },
                  "Keeping the current statement",
                )
              }
            />
          </HudSourceChip>
          {dealId ? (
            <HudSourcePicker
              open={pickerOpen}
              onClose={() => setPickerOpen(false)}
              dealId={dealId}
              locked={statementLocked}
              onChosen={onHudSourceChanged}
            />
          ) : null}
          <Box
            sx={{
              display: "grid",
              gap: 3,
              pt: 1.5,
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                md: "repeat(2, minmax(0, 1fr))",
              },
            }}
          >
            <HudColumn
              label="Sources"
              lines={hudSources}
              total={netWire.hudSourcesTotal}
              emptyText="No sources on file — our wire, borrower cash to close and any seller concession land here."
            />
            <HudColumn
              label="Uses"
              lines={hudUses}
              total={netWire.hudUsesTotal}
              emptyText="No uses on file — purchase price or payoffs, borrower-paid settlement charges and cash to borrower land here."
            />
          </Box>
        </Box>

        {/* Block 3 — the fee tie-out. The HUD balance cannot catch a fee
            difference, so what the HUD charges for our lender is compared with
            what we withheld from the wire on its own. */}
        <Box>
          <SectionHead
            label="Lender charges (fee tie-out)"
            end="HUD lender lines vs withheld from the wire"
          />
          <Callout
            sx={{ mt: 1.5 }}
            tone={
              lender.state === "off"
                ? "error"
                : lender.state === "tied"
                  ? "success"
                  : "neutral"
            }
            icon={
              lender.state === "off" ? (
                <ReportProblemOutlined sx={{ fontSize: 20 }} />
              ) : lender.state === "tied" ? (
                <CheckCircleRounded sx={{ fontSize: 20 }} />
              ) : (
                <RemoveCircleOutlineRounded sx={{ fontSize: 20 }} />
              )
            }
            title={
              lender.state === "off"
                ? `Lender charges differ by ${signedMoney(lender.variance)}`
                : lender.state === "tied"
                  ? "Lender charges tie out"
                  : lender.noHud
                    ? "No HUD to compare"
                    : "No HUD line names the lender"
            }
            body={
              lender.state === "off"
                ? lenderChargesMeaning(lender.variance)
                : lender.state === "tied"
                  ? "What the HUD charges the borrower for the lender matches what the LOS withheld from the wire, to the cent."
                  : lender.noHud
                    ? "The fee tie-out needs an extracted HUD."
                    : "The fees cannot be tied out from this statement — this is not a pass. Check the lender lines on the HUD by hand."
            }
            figure={lender.state === "off" ? signedMoney(lender.variance) : null}
          />
          <Box
            sx={{
              display: "grid",
              gap: 3,
              pt: 1.5,
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                md: "repeat(2, minmax(0, 1fr))",
              },
            }}
          >
            <HudColumn
              label="HUD charges to the lender"
              lines={lenderCharges}
              total={lenderCharges.reduce((s, l) => s + num(l?.amount), 0)}
              emptyText="No HUD line names the lender."
            />
            <HudColumn
              label="Withheld from the wire"
              lines={withheldSources}
              total={netWire.lenderWithheldTotal}
              emptyText="Nothing withheld from the wire."
            />
          </Box>
        </Box>

        {systemDetails ? (
          <Disclosure label="System details">
            <AdaptiveRows data={systemDetails} />
          </Disclosure>
        ) : null}
      </Flx>
    </PaneShell>
  );
};

TieOutPane.propTypes = {
  netWire: PropTypes.object,
  loading: PropTypes.bool,
  failed: PropTypes.bool,
  onViewPdf: PropTypes.func,
  onDownloadWorksheet: PropTypes.func,
  downloadingWorksheet: PropTypes.bool,
  dealId: PropTypes.string,
  /** Locked / Acknowledged statement: the HUD source can no longer change. */
  statementLocked: PropTypes.bool,
  /** Receives the POST hud_source response ({ netWireCalculator, extractionQueued }). */
  onHudSourceChanged: PropTypes.func,
  /** An unparsed pick was queued for extraction; the shell refetches shortly. */
  readingStatement: PropTypes.bool,
};

export default TieOutPane;
